<template>
  <div class="new-author">
    <div class="input-row">
      <div class="input-wrap" :class="{ 'has-platform': validation.platform, 'has-error': showError }">
        <div class="platform-indicator" v-if="validation.platform">
          <img class="platform-icon" :src="platformIcon" :alt="validation.platform"/>
        </div>
        <input
          v-model="url"
          @input="onInput"
          @paste="onPaste"
          @keyup.enter="addAuthor"
          @blur="touched = true"
          type="url"
          class="form-control"
          aria-label="Profile link"
        >
      </div>
      <button @click="addAuthor" class="btn btn-primary" :disabled="!url || !validation.valid">
        <i class="bi bi-plus-lg" aria-hidden="true"></i>
        Add profile
      </button>
    </div>

    <div class="feedback-error" v-if="showError">
      <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
      <span>{{ validation.error }}</span>
    </div>
    <div class="platforms" v-else>
      <span class="platforms-label">Supported</span>
      <span class="platform" v-for="pl in platforms" :key="pl.key" :class="{ 'is-current': validation.platform === pl.key }">
        <img :src="pl.icon" :alt="pl.label"/>
        {{ pl.label }}
      </span>
    </div>
  </div>
</template>

<script>
  import { validateChannel, stripQuery } from '../../../utils/socialChannels';

  export default {
    name: "new-author",
    data() {
      return {
        picture: '',
        url: '',
        touched: false,
        apiHost: this.$appSettings.getSetting('host'),
        platforms: [
          { key: 'instagram', label: 'Instagram', icon: require('../../../assets/platforms/instagram.png') },
          { key: 'tiktok', label: 'TikTok', icon: require('../../../assets/platforms/tiktok.png') },
          { key: 'youtube', label: 'YouTube', icon: require('../../../assets/platforms/youtube.png') },
          { key: 'facebook', label: 'Facebook', icon: require('../../../assets/platforms/facebook.png') },
          { key: 'twitch', label: 'Twitch', icon: require('../../../assets/platforms/twitch.png') }
        ]
      }
    },
    computed: {
      validation() {
        return validateChannel(this.url);
      },
      showError() {
        return this.touched && this.url.length > 0 && !this.validation.valid;
      },
      platformIcon() {
        if (!this.validation.platform) return '';
        return require('../../../assets/platforms/' + this.validation.platform + '.png');
      }
    },
    methods: {
      onInput() {
        const stripped = stripQuery(this.url);
        if (stripped !== this.url) {
          this.url = stripped;
        }
      },
      onPaste(event) {
        const pasted = (event.clipboardData || window.clipboardData).getData('text');
        if (pasted && pasted.indexOf('?') !== -1) {
          event.preventDefault();
          this.url = stripQuery(pasted);
        }
      },
      addAuthor() {
        const cleaned = stripQuery(this.url);
        this.url = cleaned;
        this.touched = true;

        if (!cleaned) {
          this.$notify({
            group: 'default',
            type: 'error',
            text: 'Paste a profile link first'
          });
          return;
        }

        const result = validateChannel(cleaned);
        if (!result.valid) {
          this.$notify({
            group: 'default',
            type: 'error',
            text: result.error
          });
          return;
        }

        const finalUrl = result.cleanUrl;
        const that = this;
        this.checkAuthor(finalUrl, function(data) {
          if (data.body.data.check) {
            that.$emit('addAuthor', {
              picture: that.picture,
              url: finalUrl
            });
            that.picture = '';
            that.url = '';
            that.touched = false;
          } else {
            that.$notify({
              group: 'default',
              type: 'error',
              text: 'This profile is already assigned to influencer #' + data.body.data.influencerId
            });
          }
        });
      },
      checkAuthor(url, callback) {
        this.$http.post(this.apiHost + 'influencers/check/author', {
          url: url
        },
          {
            params: {
              token: this.$auth.getToken()
          }
        }).then(function(data) {
          callback(data);
        });
      }
    }
  }
</script>

<style scoped>
  .new-author {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
  }
  .input-row {
    display: flex;
    gap: 8px;
    align-items: stretch;
  }
  .input-wrap {
    position: relative;
    flex: 1;
  }
  .input-wrap.has-platform .form-control {
    padding-left: 42px;
  }
  .platform-indicator {
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .platform-icon {
    width: 22px;
    height: 22px;
    border-radius: 4px;
  }
  .form-control {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .form-control:focus {
    border-color: #265a88;
    box-shadow: 0 0 0 3px rgba(38, 90, 136, 0.12);
    outline: none;
  }
  .input-wrap.has-error .form-control {
    border-color: #dc2626;
  }
  .input-wrap.has-error .form-control:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12);
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .feedback-error {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 8px;
    font-size: 12.5px;
    color: #b91c1c;
    line-height: 1.4;
  }
  .feedback-error .bi {
    flex-shrink: 0;
    margin-top: 2px;
  }

  .platforms {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    margin-top: 10px;
    font-size: 12.5px;
    color: #6b7280;
  }
  .platforms-label {
    color: #6b7280;
  }
  .platform {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #475569;
  }
  .platform img {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    opacity: 0.85;
  }
  .platform.is-current {
    color: #265a88;
    font-weight: 600;
  }
  .platform.is-current img { opacity: 1; }

  @media (max-width: 540px) {
    .input-row {
      flex-direction: column;
    }
    .btn {
      width: 100%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/influencer/author/NewAuthor.vue